import { useDispatch, useHistory, useSelector } from 'umi';
import { getGameList } from '../services';
import { getListMenuAndIndexPage } from '@op/hooks/useGetPageSourceRule';
import {
  getFilterValueStorage,
  setSessionStorage,
} from '@op/components/FilterCommon/utils/storage';
import { gameInfoType } from '@op/types/common'; 
import { JumpType, navigate } from '@home/utils/utils';
import { ListMenuItemType } from '@op/types/menu';

export function getGameInfo(wholeGameInfo: any) {
  return {
    edition_unified_id: wholeGameInfo?.edition_unified_id,
    entity_type: wholeGameInfo?.entity_type,
    id_type: wholeGameInfo?.id_type,
  };
}

/**
 * 寻找真实的game
 * @param gameList 游戏列表
 * @param id  游戏id
 * @returns [当前游戏信息， 父级游戏信息 || false]
 */
export const findGameInfo = (
  gameList: Array<any> = [],
  id: string,
): [any, any | boolean] | undefined => {
  if (!Array.isArray(gameList)) return;
  for (const game of gameList) {
    if (game?.edition_unified_id !== id) {
      if (game?.mobile_edition_list) {
        const newGame = findGameInfo(game.mobile_edition_list, id);
        if (newGame) {
          const [data] = newGame;
          return [data, game];
        }
      }
    } else {
      return [game, false];
    }
  }
};

/**
 * 获取单个游戏数据，先查找缓存，如果没有就查找接口
 * @param edition_unified_id
 * @returns
 */
export const getServerGameInfo = async (edition_unified_id: string) => {
  try {
    const serverData = await getGameList({
      need_mobile_editions: true,
      edition_unified_ids: [edition_unified_id],
    });
    const [game] = findGameInfo(serverData, edition_unified_id) || [];
    return game;
  } catch (error) {
    return {};
  }
};

export const changeStoreGameInfo = async (
  gameInfo: any,
  dispatch: any,
  requestGameInfo = false,
  changeGameVisible = false,
) => {
  dispatch({
    type: 'opinionCommon/save',
    payload: {
      listMenu: [],
      listMenuLoading: true,
      gameInfo: {},
      wholeGameInfo: {},
      gameInfoLoading: true,
      excludeSource: [],
      hidePageIds: [],
      showGameInfo: gameInfo,
    },
  });
  let { edition_unified_id, entity_type, id_type } = gameInfo;
  if (requestGameInfo) {
    gameInfo = await getServerGameInfo(edition_unified_id);
    if (gameInfo) {
      edition_unified_id = gameInfo.edition_unified_id;
      entity_type = gameInfo.entity_type;
      id_type = gameInfo.id_type;
    }
  }
  // props: listMenu excludeSource hidePageIds
  const { url, ...props } = await getListMenuAndIndexPage(gameInfo);

  dispatch({
    type: 'opinionCommon/save',
    payload: {
      wholeGameInfo: gameInfo || {},
      gameInfo: {
        edition_unified_id,
        entity_type,
        id_type,
      },
      listMenuLoading: false,
      gameInfoLoading: false,
      changeGameVisible,
      ...props,
    },
  });
  return url;
};

export const handleGameClick = async (
  cvm: any,
  dispatch: any,
  history: any,
  requestGameInfo = false,
  changeGameVisible = false,
  jumpType?: JumpType,
  filter?: any,
) => {
  const url = await changeStoreGameInfo(
    cvm,
    dispatch,
    requestGameInfo,
    changeGameVisible,
  );

  if (jumpType === JumpType.Window) {
    navigate(url, { jumpType, windowParams: filter });
    return;
  }

  history.push({ pathname: url });
};

export const useGameOperations = (props?: {
  jumpType?: JumpType;
  filter?: any;
}) => {
  const { jumpType, filter } = props || {};
  const history = useHistory();
  const dispatch = useDispatch();

  const handleChangeGame = (cvm: any, requestGameInfo = false) => {
    handleGameClick(
      cvm,
      dispatch,
      history,
      requestGameInfo,
      undefined,
      jumpType,
      filter,
    );
  };

  const handleChangeGameStore = (
    cvm: any,
    requestGameInfo = false,
    changeGameVisible = false,
  ) => {
    changeStoreGameInfo(cvm, dispatch, requestGameInfo, changeGameVisible);
  };

  return {
    handleChangeGame,
    handleChangeGameStore,
  };
};

export function useGoToCompetitorGame() {
  const { handleChangeGame } = useGameOperations();
  const { gameInfo } = useSelector(({ opinionCommon }: any) => {
    return {
      gameInfo: opinionCommon.gameInfo,
    };
  });
  const goToCompetitorGame = async (cvm: any) => {
    // 获取当前过滤器中的内容
    const sessionStorage = getFilterValueStorage(gameInfo);
    // 设置过滤器的值
    setSessionStorage(sessionStorage, cvm, true);
    // 跳转到其他游戏
    handleChangeGame(cvm, true);
  };

  return {
    goToCompetitorGame,
  };
}

export function isSubVersion(gameInfo: gameInfoType) {
  if (gameInfo?.entity_type !== 'mobile') return false;

  return gameInfo?.id_type !== 'unified_id';
}

export function gameIsNormalLevel(gameInfo: any) {
  return gameInfo?.priority === 'normal';
}

interface FindMenuItemOptions {
  /** 查找键：id 或 name_en */
  key?: 'id' | 'name_en';
  /** 父级菜单名称，如果传入则需要匹配父级 */
  parentMenuName?: string;
}

/**
 * 通过菜单名称或者菜单id查找具体菜单
 * @param list 菜单列表
 * @param menuName 需要查找的菜单标识符
 * @param options 查找选项
 * @returns 找到的菜单项，未找到则返回null
 */
export function findMenuItem(
  list: Array<ListMenuItemType> = [],
  menuName: string,
  options: FindMenuItemOptions = {},
) {
  const { key = 'name_en', parentMenuName } = options;

  // 在指定父级菜单中查找目标菜单
  function searchInParent(parentItems: Array<any>): any {
    for (const item of parentItems) {
      if (item[key] === menuName) {
        return item;
      }

      if (item.opinion_list && item.opinion_list.length > 0) {
        const found = searchInParent(item.opinion_list);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  // 查找所有匹配的父级菜单，并在其中搜索目标菜单
  function findAndSearchInParents(items: Array<any>): any {
    for (const item of items) {
      // 如果当前菜单匹配父级菜单名
      if (item[key] === parentMenuName) {
        // 在当前父级菜单的子级中查找目标菜单
        if (item.opinion_list && item.opinion_list.length > 0) {
          const found = searchInParent(item.opinion_list);
          if (found) {
            return found;
          }
        }
      }

      // 递归查找子菜单中是否有匹配的父级菜单
      if (item.opinion_list && item.opinion_list.length > 0) {
        const foundInChildren = findAndSearchInParents(item.opinion_list);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }
    return null;
  }

  // 没有父级要求时的普通递归搜索
  function searchWithoutParent(items: Array<any>): any {
    for (const item of items) {
      if (item[key] === menuName) {
        return item;
      }

      if (item.opinion_list && item.opinion_list.length > 0) {
        const found = searchWithoutParent(item.opinion_list);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  // 根据是否有parentMenuName选择搜索策略
  if (parentMenuName) {
    return findAndSearchInParents(list);
  }
  return searchWithoutParent(list);
}

/**
 * 判断游戏是否为高优先级游戏
 * @param wholeGameInfo
 * @returns
 */
export function getIsHighGame(wholeGameInfo: { priority: string }) {
  return ['emergency', 'high'].includes(wholeGameInfo?.priority);
}
